import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import {
  Person,
  Radio,
  Event,
  Settings,
  Image,
  Dashboard as DashboardIcon,
  Info,
  Videocam,
  Download,
  ContactPhone,
  Image as ImageIcon,
  Forum,
  CommentsDisabled,
  CheckCircle
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { radioInfoService } from '../services/radioInfoService';
import { useAuth } from '../contexts/AuthContext';
import UserManagement from '../components/dashboard/UserManagement';
import ProgramManagement from '../components/dashboard/ProgramManagement';
import EventManagement from '../components/dashboard/EventManagement';
import BannerManagement from '../components/dashboard/BannerManagement';
import DownloadManagement from '../components/dashboard/DownloadManagement';
import ContactManagement from '../components/dashboard/ContactManagement';
import RadioInfoManagement from '../components/dashboard/RadioInfoManagement';
import LiveCameraManagement from '../components/dashboard/LiveCameraManagement';
import DJProfileManagement from '../components/dashboard/DJProfileManagement';

import DJProgramManagement from '../components/dashboard/DJProgramManagement';
import DJDownloads from '../components/dashboard/DJDownloads';
import DJRadioInfo from '../components/dashboard/DJRadioInfo';
import DJLiveCamera from '../components/dashboard/DJLiveCamera';
import DJChatBackgroundManagement from '../components/dashboard/DJChatBackgroundManagement';

const Dashboard = () => {
  const { user, isAdmin, isDJ, isSuperAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const queryClient = useQueryClient();

  // Buscar informacoes ativas da radio para controle do chat
  const { data: radioInfoData } = useQuery({
    queryKey: ['activeRadioInfo'],
    queryFn: () => radioInfoService.getActiveRadioInfo(),
    enabled: !!isAdmin,
  });

  const isChatActive = radioInfoData?.data?.radioInfo?.is_chat_enabled !== false;

  const toggleChatMutation = useMutation({
    mutationFn: (newStatus) => radioInfoService.toggleChat({ isChatEnabled: newStatus }),
    onSuccess: (response) => {
      const message = response?.data?.message || 'Status do bate-papo atualizado com sucesso!';
      toast.success(message);
      queryClient.invalidateQueries(['activeRadioInfo']);
      queryClient.invalidateQueries(['radioInfo']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Erro ao alterar status do bate-papo');
    },
  });

  const handleToggleChat = (newStatus) => {
    toggleChatMutation.mutate(newStatus);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'superadmin':
        return 'Super Administrador';
      case 'admin':
        return 'Administrador';
      case 'dj':
        return 'DJ';
      case 'organizer':
        return 'Organizador';
      default:
        return 'Usuário';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Ativo';
      case 'pending':
        return 'Pendente';
      case 'inactive':
        return 'Inativo';
      default:
        return 'Desconhecido';
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderTabContent = () => {
    if (isAdmin) {
      switch (activeTab) {
        case 0:
          return <UserManagement />;
        case 1:
          return <ProgramManagement />;
        case 2:
          return <EventManagement />;
        case 3:
          return <BannerManagement />;
        case 4:
          return <DownloadManagement />;
        case 5:
          return <ContactManagement />;
        case 6:
          return <RadioInfoManagement />;
        case 7:
          return <LiveCameraManagement />;
        default:
          return <UserManagement />;
      }

    } else if (isDJ) {
      switch (activeTab) {
        case 0:
          return <DJProfileManagement />;
        case 1:
          return <DJProgramManagement />;
        case 2:
          return <DJDownloads />;
        case 3:
          return <DJRadioInfo />;
        case 4:
          return <DJLiveCamera />;
        case 5:
          return <DJChatBackgroundManagement />;
        default:
          return <DJProfileManagement />;
      }
    }
    return null;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: 'Orbitron',
            fontWeight: 700,
            color: '#FF6B35',
            mb: 1,
          }}
        >
          {getGreeting()}, {user?.name}!
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.7 }}>
          Bem-vindo ao seu dashboard da NostalBeat
        </Typography>
      </Box>


      {/* Status Messages */}
      {user?.status === 'pending' && (
        <Card
          sx={{
            bgcolor: 'rgba(255, 152, 0, 0.1)',
            border: '1px solid rgba(255, 152, 0, 0.3)',
            mb: 3,
          }}
        >
          <CardContent>
            <Typography variant="h6" sx={{ color: '#FF9800', mb: 1 }}>
              ⏳ Aguardando Aprovação
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Seu cadastro está sendo analisado pela administração.
              Você receberá um email quando sua conta for aprovada.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Quick Control Card for Radio Chat (Admin / Super Admin) */}
      {isAdmin && (
        <Card
          sx={{
            bgcolor: 'rgba(15, 17, 26, 0.85)',
            backdropFilter: 'blur(10px)',
            border: isChatActive
              ? '1px solid rgba(0, 230, 118, 0.35)'
              : '1px solid rgba(255, 23, 68, 0.45)',
            boxShadow: isChatActive
              ? '0 4px 20px rgba(0, 230, 118, 0.12)'
              : '0 4px 20px rgba(255, 23, 68, 0.18)',
            borderRadius: 3,
            mb: 3.5,
            transition: 'all 0.3s ease',
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: isChatActive ? 'rgba(0, 230, 118, 0.12)' : 'rgba(255, 23, 68, 0.12)',
                    border: `1px solid ${isChatActive ? 'rgba(0, 230, 118, 0.4)' : 'rgba(255, 23, 68, 0.4)'}`,
                    flexShrink: 0,
                  }}
                >
                  <Forum sx={{ color: isChatActive ? '#00E676' : '#FF1744', fontSize: 28 }} />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: 'Orbitron, sans-serif',
                        fontWeight: 700,
                        fontSize: { xs: '1rem', sm: '1.15rem' },
                        color: '#FFF',
                      }}
                    >
                      Bate-Papo da Rádio
                    </Typography>
                    <Chip
                      label={isChatActive ? 'NO AR (ONLINE)' : 'FORA DO AR (PAUSADO)'}
                      size="small"
                      sx={{
                        bgcolor: isChatActive ? 'rgba(0, 230, 118, 0.2)' : 'rgba(255, 23, 68, 0.2)',
                        color: isChatActive ? '#00E676' : '#FF5252',
                        border: `1px solid ${isChatActive ? 'rgba(0, 230, 118, 0.5)' : 'rgba(255, 23, 68, 0.5)'}`,
                        fontWeight: 800,
                        fontSize: '0.72rem',
                        letterSpacing: '0.5px',
                        fontFamily: 'Orbitron, sans-serif',
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem' }}>
                    {isChatActive
                      ? 'O bate-papo dos ouvintes está ATIVO no site. Qualquer visitante pode interagir normalmente.'
                      : 'O bate-papo está TEMPORARIAMENTE PAUSADO. Os ouvintes veem o aviso de pausa no lugar do chat.'}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: { xs: '100%', md: 'auto' } }}>
                <Button
                  variant="contained"
                  fullWidth={false}
                  disabled={toggleChatMutation.isPending}
                  onClick={() => handleToggleChat(!isChatActive)}
                  startIcon={isChatActive ? <CommentsDisabled /> : <CheckCircle />}
                  sx={{
                    py: 1.2,
                    px: 3,
                    fontWeight: 800,
                    fontSize: '0.85rem',
                    fontFamily: 'Orbitron, sans-serif',
                    letterSpacing: '0.5px',
                    borderRadius: 2,
                    textTransform: 'none',
                    background: isChatActive
                      ? 'linear-gradient(135deg, #FF1744 0%, #D50000 100%)'
                      : 'linear-gradient(135deg, #00E676 0%, #00C853 100%)',
                    boxShadow: isChatActive
                      ? '0 4px 15px rgba(255, 23, 68, 0.4)'
                      : '0 4px 15px rgba(0, 230, 118, 0.4)',
                    '&:hover': {
                      filter: 'brightness(1.15)',
                    },
                  }}
                >
                  {toggleChatMutation.isPending
                    ? 'Atualizando...'
                    : isChatActive
                      ? 'Tirar Bate-Papo do Ar'
                      : 'Voltar Bate-Papo ao Ar'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Admin Tabs */}
      {isAdmin && (
        <Paper sx={{ bgcolor: 'rgba(10, 10, 10, 0.5)' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                fontWeight: 600,
                textTransform: 'none',
                minHeight: 48,
                '&.Mui-selected': {
                  color: '#FF6B35',
                },
              },
              '& .MuiTabs-indicator': {
                bgcolor: '#FF6B35',
              }
            }}
          >
            <Tab
              icon={<Person />}
              label="Usuários"
              iconPosition="start"
            />
            <Tab
              icon={<Radio />}
              label="Programas"
              iconPosition="start"
            />
            <Tab
              icon={<Event />}
              label="Eventos"
              iconPosition="start"
            />

            <Tab
              icon={<Image />}
              label="Banners"
              iconPosition="start"
            />
            <Tab
              icon={<Download />}
              label="Downloads"
              iconPosition="start"
            />
            <Tab
              icon={<ContactPhone />}
              label="Contatos"
              iconPosition="start"
            />
            <Tab
              icon={<Info />}
              label="Informações da Rádio"
              iconPosition="start"
            />
            <Tab
              icon={<Videocam />}
              label="Câmera ao Vivo"
              iconPosition="start"
            />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {renderTabContent()}
          </Box>
        </Paper>
      )}

      {/* DJ Content */}
      {isDJ && !isAdmin && (
        <Paper sx={{ bgcolor: 'rgba(10, 10, 10, 0.5)' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                fontWeight: 600,
                textTransform: 'none',
                minHeight: 48,
                '&.Mui-selected': {
                  color: '#FF6B35',
                },
              },
              '& .MuiTabs-indicator': {
                bgcolor: '#FF6B35',
              }
            }}
          >
            <Tab
              icon={<Person />}
              label="Meu Perfil"
              iconPosition="start"
            />
            <Tab
              icon={<Radio />}
              label="Meus Programas"
              iconPosition="start"
            />
            <Tab
              icon={<Download />}
              label="Downloads"
              iconPosition="start"
            />
            <Tab
              icon={<Info />}
              label="Informações da Rádio"
              iconPosition="start"
            />
            <Tab
              icon={<Videocam />}
              label="Câmera ao Vivo"
              iconPosition="start"
            />
            <Tab
              icon={<ImageIcon />}
              label="Background Chat"
              iconPosition="start"
            />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {renderTabContent()}
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default Dashboard;
